type CacheEntry<T> = {
  data: T;
  expiresAt: number;
};

export type BootstrapDeduper<T> = {
  load: (key: string, loader: () => Promise<T>, ttlMs?: number) => Promise<T>;
  clear: (key?: string) => void;
};

export function createBootstrapDeduper<T>(): BootstrapDeduper<T> {
  const cache = new Map<string, CacheEntry<T>>();
  const inflight = new Map<string, Promise<T>>();
  const keyGenerations = new Map<string, number>();
  let globalGeneration = 0;

  return {
    async load(key, loader, ttlMs = 5_000) {
      const cached = cache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        return cached.data;
      }

      const existing = inflight.get(key);
      if (existing) {
        return existing;
      }

      const requestGlobalGeneration = globalGeneration;
      const requestKeyGeneration = keyGenerations.get(key) ?? 0;
      const request = loader()
        .then((data) => {
          if (
            requestGlobalGeneration === globalGeneration
            && requestKeyGeneration === (keyGenerations.get(key) ?? 0)
          ) {
            cache.set(key, {
              data,
              expiresAt: Date.now() + ttlMs,
            });
          }
          return data;
        })
        .finally(() => {
          if (inflight.get(key) === request) {
            inflight.delete(key);
          }
        });

      inflight.set(key, request);
      return request;
    },

    clear(key) {
      if (key) {
        cache.delete(key);
        inflight.delete(key);
        keyGenerations.set(key, (keyGenerations.get(key) ?? 0) + 1);
        return;
      }

      cache.clear();
      inflight.clear();
      keyGenerations.clear();
      globalGeneration += 1;
    },
  };
}
